import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiUrl } from "../lib/apiBase";
import socket from "../lib/socket";

let youtubeApiPromise = null;
const MUSIC_AUDIO_PREF_KEY = "ga_website_music_audio_v1";

function loadYouTubeApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve, reject) => {
    const previousReady = window.onYouTubeIframeAPIReady;
    let settled = false;

    const finish = () => {
      if (settled) return;
      if (!window.YT?.Player) return;
      settled = true;
      resolve(window.YT);
    };

    window.onYouTubeIframeAPIReady = () => {
      try {
        previousReady?.();
      } catch {}
      finish();
    };

    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      script.onerror = () => {
        if (!settled) {
          settled = true;
          reject(new Error("youtube_api_load_failed"));
        }
      };
      document.head.appendChild(script);
    }

    const started = Date.now();
    const timer = window.setInterval(() => {
      if (window.YT?.Player) {
        window.clearInterval(timer);
        finish();
      } else if (Date.now() - started > 10000) {
        window.clearInterval(timer);
        if (!settled) {
          settled = true;
          reject(new Error("youtube_api_timeout"));
        }
      }
    }, 100);
  });

  return youtubeApiPromise;
}

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clampVolume(value) {
  return Math.max(0, Math.min(100, Math.round(safeNumber(value, 100))));
}

function loadAudioPreference() {
  try {
    const parsed = JSON.parse(localStorage.getItem(MUSIC_AUDIO_PREF_KEY) || "null");
    return {
      volume: clampVolume(parsed?.volume ?? 100),
      muted: Boolean(parsed?.muted),
    };
  } catch {
    return { volume: 100, muted: false };
  }
}

function saveAudioPreference(volume, muted) {
  try {
    localStorage.setItem(
      MUSIC_AUDIO_PREF_KEY,
      JSON.stringify({ volume: clampVolume(volume), muted: Boolean(muted) })
    );
  } catch {
    // Viewer audio preference is optional.
  }
}

const styles = {
  wrap: {
    marginTop: 10,
    border: "1px solid rgba(255,255,255,.12)",
    borderRadius: 14,
    background: "linear-gradient(145deg,rgba(18,7,13,.97),rgba(5,6,10,.96))",
    padding: 10,
    color: "#fff",
  },
  row: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  copy: { minWidth: 0, flex: 1 },
  kicker: { fontSize: 9, fontWeight: 1000, letterSpacing: ".12em", color: "#ff9da5" },
  title: {
    fontSize: 12,
    fontWeight: 950,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginTop: 2,
  },
  meta: {
    fontSize: 10,
    color: "rgba(255,255,255,.58)",
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  playerShell: {
    width: 128,
    height: 72,
    borderRadius: 9,
    overflow: "hidden",
    background: "#000",
    flex: "0 0 auto",
  },
  playerHost: {
    width: "100%",
    height: "100%",
    background: "#000",
  },
  audioRow: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    marginTop: 9,
    paddingTop: 8,
    borderTop: "1px solid rgba(255,255,255,.08)",
    minWidth: 0,
  },
  muteButton: {
    minHeight: 32,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,.16)",
    background: "rgba(255,255,255,.07)",
    color: "#fff",
    padding: "0 11px",
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  range: {
    flex: "1 1 auto",
    minWidth: 70,
    accentColor: "#ff4f68",
    cursor: "pointer",
  },
  percent: {
    width: 38,
    flex: "0 0 38px",
    textAlign: "right",
    fontSize: 10,
    fontWeight: 850,
    color: "rgba(255,255,255,.72)",
  },
};

export default function ChannelMusicPlayer({ channelSlug = "", enabled = false }) {
  const slug = String(channelSlug || "").trim().toLowerCase();
  const [initialAudioPreference] = useState(() => loadAudioPreference());
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [musicVolume, setMusicVolume] = useState(initialAudioPreference.volume);
  const [musicMuted, setMusicMuted] = useState(initialAudioPreference.muted);
  const playerRef = useRef(null);
  const latestPositionRef = useRef(0);
  const latestPausedRef = useRef(false);
  const mountedRef = useRef(true);
  const endedTrackRef = useRef("");
  const audioPreferenceRef = useRef(initialAudioPreference);
  const playerHostId = useMemo(
    () => "ga-website-music-" + Math.random().toString(36).slice(2),
    []
  );

  audioPreferenceRef.current = {
    volume: clampVolume(musicVolume),
    muted: Boolean(musicMuted),
  };

  const applyViewerAudio = useCallback((player) => {
    if (!player) return;
    const preference = audioPreferenceRef.current;
    try {
      player.setVolume?.(clampVolume(preference.volume));
      if (preference.muted || clampVolume(preference.volume) === 0) {
        player.mute?.();
      } else {
        player.unMute?.();
      }
    } catch {}
  }, []);

  const load = useCallback(async () => {
    if (!enabled || !slug) {
      setState(null);
      return;
    }
    try {
      const response = await fetch(apiUrl("/channel-music/" + encodeURIComponent(slug)), {
        cache: "no-store",
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.ok || !data?.enabled) {
        setState(null);
        return;
      }
      setState(data.state || null);
      setError("");
    } catch {
      setError("Music status unavailable.");
    }
  }, [enabled, slug]);

  const reportTrackEnded = useCallback(
    async (player, trackId, videoId) => {
      if (!slug || !trackId || endedTrackRef.current === trackId) return;
      endedTrackRef.current = trackId;

      const durationSeconds = Math.max(0, safeNumber(player?.getDuration?.(), 0));
      const positionSeconds = Math.max(
        0,
        safeNumber(player?.getCurrentTime?.(), 0),
        durationSeconds
      );

      try {
        const response = await fetch(
          apiUrl("/channel-music/" + encodeURIComponent(slug) + "/ended"),
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              trackId,
              videoId,
              positionSeconds,
              durationSeconds,
            }),
          }
        );
        const data = await response.json().catch(() => null);
        if (response.ok && data?.state) {
          setState(data.state);
          setError("");
          return;
        }
      } catch {
        // The normal polling path below is the fallback.
      }

      window.setTimeout(load, 700);
    },
    [load, slug]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !slug) return undefined;
    load();
    const timer = window.setInterval(load, 5000);
    return () => window.clearInterval(timer);
  }, [enabled, slug, load]);

  useEffect(() => {
    if (!enabled || !slug) return undefined;
    const onUpdate = (nextState) => {
      if (!nextState || String(nextState?.channelSlug || "").toLowerCase() !== slug) return;
      setState(nextState);
      setError("");
    };
    socket.on("channel-music:update", onUpdate);
    return () => socket.off("channel-music:update", onUpdate);
  }, [enabled, slug]);

  const current = state?.nowPlaying || null;
  const queued = Array.isArray(state?.queue) ? state.queue : [];
  const position = Math.max(0, Math.floor(safeNumber(state?.positionSeconds, 0)));
  latestPositionRef.current = position;
  latestPausedRef.current = Boolean(state?.paused);

  useEffect(() => {
    endedTrackRef.current = "";
  }, [current?.id]);

  useEffect(() => {
    saveAudioPreference(musicVolume, musicMuted);
    applyViewerAudio(playerRef.current);
  }, [applyViewerAudio, musicMuted, musicVolume]);

  useEffect(() => {
    if (!enabled || !current?.videoId) {
      try {
        playerRef.current?.destroy?.();
      } catch {}
      playerRef.current = null;
      setAutoplayBlocked(false);
      return undefined;
    }

    let cancelled = false;
    const trackId = String(current.id || "");
    const videoId = String(current.videoId || "");
    const initialStart = Math.max(0, latestPositionRef.current - 1);

    loadYouTubeApi()
      .then((YT) => {
        if (cancelled || !mountedRef.current) return;

        try {
          playerRef.current?.destroy?.();
        } catch {}

        const player = new YT.Player(playerHostId, {
          videoId,
          width: "100%",
          height: "100%",
          playerVars: {
            autoplay: latestPausedRef.current ? 0 : 1,
            controls: 1,
            playsinline: 1,
            rel: 0,
            modestbranding: 1,
            start: initialStart,
          },
          events: {
            onReady: (event) => {
              if (cancelled) return;
              try {
                applyViewerAudio(event.target);
                if (latestPausedRef.current) {
                  event.target.pauseVideo();
                } else {
                  event.target.playVideo();
                }
              } catch {}
            },
            onStateChange: (event) => {
              if (cancelled) return;
              if (event.data === YT.PlayerState.PLAYING) {
                applyViewerAudio(event.target);
                setAutoplayBlocked(false);
                setError("");
              } else if (event.data === YT.PlayerState.ENDED) {
                reportTrackEnded(event.target, trackId, videoId);
              }
            },
            onAutoplayBlocked: () => {
              if (!cancelled) setAutoplayBlocked(true);
            },
            onError: () => {
              if (!cancelled) setError("This song could not play in the website player.");
            },
          },
        });

        playerRef.current = player;
      })
      .catch(() => {
        if (!cancelled) setError("Music player unavailable.");
      });

    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy?.();
      } catch {}
      playerRef.current = null;
    };
  }, [
    applyViewerAudio,
    current?.id,
    current?.videoId,
    enabled,
    playerHostId,
    reportTrackEnded,
  ]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !current?.id) return;

    try {
      if (state?.paused) {
        player.pauseVideo?.();
      } else {
        const currentTime = safeNumber(player.getCurrentTime?.(), 0);
        const desiredPosition = Math.max(0, latestPositionRef.current);
        if (Math.abs(currentTime - desiredPosition) > 8) {
          player.seekTo?.(desiredPosition, true);
        }
        applyViewerAudio(player);
        player.playVideo?.();
      }
    } catch {}
  }, [applyViewerAudio, current?.id, state?.paused]);

  useEffect(() => {
    if (!autoplayBlocked || !current?.id) return undefined;

    const startFromGesture = () => {
      const player = playerRef.current;
      if (!player) return;
      try {
        player.seekTo?.(Math.max(0, latestPositionRef.current - 1), true);
        applyViewerAudio(player);
        player.playVideo?.();
        setAutoplayBlocked(false);
      } catch {}
    };

    window.addEventListener("pointerdown", startFromGesture, true);
    window.addEventListener("touchstart", startFromGesture, true);
    window.addEventListener("keydown", startFromGesture, true);
    return () => {
      window.removeEventListener("pointerdown", startFromGesture, true);
      window.removeEventListener("touchstart", startFromGesture, true);
      window.removeEventListener("keydown", startFromGesture, true);
    };
  }, [applyViewerAudio, autoplayBlocked, current?.id]);

  if (!enabled || !slug) return null;

  const metaText = current
    ? (state?.paused ? "Paused" : "Now playing") +
      " • requested by " +
      (current?.requestedBy?.username || "viewer") +
      (queued.length ? " • " + queued.length + " queued" : "") +
      (autoplayBlocked ? " • tap anywhere once for audio" : "") +
      (error ? " • " + error : "")
    : "Queue ready • type !sr song name, !sr artist - song, or paste a YouTube link";

  const effectiveMuted = musicMuted || musicVolume === 0;

  return (
    <section style={styles.wrap} aria-label="Channel music">
      <div style={styles.row}>
        {current?.videoId ? (
          <div style={styles.playerShell}>
            <div id={playerHostId} style={styles.playerHost} />
          </div>
        ) : null}

        <div style={styles.copy}>
          <div style={styles.kicker}>🎵 GASTREAMING WEBSITE MUSIC</div>
          <div style={styles.title}>{current?.title || "Queue is ready"}</div>
          <div style={styles.meta}>{metaText}</div>
        </div>
      </div>

      <div style={styles.audioRow}>
        <button
          type="button"
          style={styles.muteButton}
          aria-pressed={effectiveMuted}
          onClick={() => setMusicMuted((value) => !value)}
          title="Mute or unmute website music only"
        >
          {effectiveMuted ? "🔇 Music muted" : "🔊 Music"}
        </button>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={musicVolume}
          aria-label="Website music volume"
          style={styles.range}
          onChange={(event) => {
            const next = clampVolume(event.target.value);
            setMusicVolume(next);
            if (next > 0 && musicMuted) setMusicMuted(false);
          }}
        />
        <span style={styles.percent}>{effectiveMuted ? "0%" : `${musicVolume}%`}</span>
      </div>
    </section>
  );
}
